import { EffectCallback, RefObject, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { ERequestStatus } from '../../types/requestStatus';
import { selectListingAppendType } from '../../selectors/settings/selectListingAppendType';
import { setInitialPageNumber, setListingAppendType } from '../../actions/settings';
import { TDependencyName, useSensitiveParams } from '../useSensitiveParams';
import { selectInitialPageNumber } from '../../selectors/settings/selectInitialPageNumber';
import { preparePageNumber } from '../preparePageNumber';
import { IEventTrackingData } from '../useEventTracking';
import { selectAuthorArticles } from '../../selectors/authorArticles';

interface IUseGetListingOptions<T> {
  dependencyNames?: TDependencyName[];
  eventTrackerData?: IEventTrackingData;
  scrollRef?: RefObject<T>;
}

/**
 * Выполняет callback для запроса листинга
 */
export const useGetAuthorArticles = <T extends HTMLElement = HTMLDivElement>(
  callback: EffectCallback,
  { dependencyNames = ['page'], scrollRef }: IUseGetListingOptions<T> = {},
) => {
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const qsPage = preparePageNumber(searchParams.get('page') as string);
  const dispatch = useDispatch();
  const { status: listingStatus } = useSelector(selectAuthorArticles);
  const appendType = useSelector(selectListingAppendType);
  const key = useSensitiveParams(dependencyNames);
  const [prevKey, setPrevKey] = useState(key);
  const initialPageNumber = useSelector(selectInitialPageNumber);

  /**
   * Запускает callback, если хотя бы соблюдено одно из условий:
   * - список находится в статусе инициализации
   * - слепок значений из чувствительных ключей изменился
   */
  useEffect(() => {
    if (listingStatus !== ERequestStatus.Initial && key === prevKey) {
      return;
    }

    /**
     * Переписывает индекс страницы инициализации
     * только для режима set
     */
    if (appendType === 'set') {
      dispatch(setInitialPageNumber(qsPage));
    }

    callback();
    setPrevKey(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, listingStatus, prevKey]);

  /**
   * Скролим наверх пока идет загрузка
   * (не для режима подгрузки)
   */
  useEffect(() => {
    if (listingStatus !== ERequestStatus.Loading || appendType === 'append') {
      return;
    }

    if (scrollRef?.current) {
      scrollRef.current.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [appendType, listingStatus, scrollRef]);

  /**
   * Сбрасываем тип сохранения материалов на дефолтный
   * в любом конечном статусе запроса
   */
  useEffect(() => {
    if (listingStatus === ERequestStatus.Loading) {
      return;
    }

    dispatch(setListingAppendType('set'));
  }, [dispatch, listingStatus]);

  /**
   * initialPageNumber не должен быть меньше qsPage
   */
  useEffect(() => {
    if (qsPage < initialPageNumber) {
      dispatch(setInitialPageNumber(qsPage));
    }
  }, [dispatch, initialPageNumber, qsPage]);
};
